<script setup lang="ts">
import type { BaseContent } from "@gasparigit/omnia-sdk";
import { InfoIcon, ShareIcon } from "lucide-vue-next";
import type { GenericObject } from "vee-validate";
import type { InputHTMLAttributes } from "vue";
import { z } from "zod";
import { processError, requiredError, successToast } from "~/lib/utils/errors";
import type { ConfigItem } from "../ui/auto-form";

const omniaSdk = useOmniaSdk();

const shareable = defineModel<BaseContent>("shareable", { required: true });
const opened = defineModel<boolean>("opened");
const loading = ref<boolean>(false);
const emits = defineEmits(["success"]);

const formSchema = z.object({
  email_to: z
    .string(requiredError)
    .trim()
    .email("Email non valida")
    .describe("Email"),
  message: z
    .string(requiredError)
    .describe("Messaggio")
    .max(500, "Massimo 500 caratteri")
    .nullable()
    .optional(),
});

const form = useForm({ validationSchema: toTypedSchema(formSchema) });
const captchaToken = ref<string>();

const fieldConfig: Record<string, ConfigItem> = {
  message: {
    component: "textarea",
    inputProps: {
      maxlength: 500,
      rows: 6,
    } as InputHTMLAttributes,
  },
};

async function sendShare(values: GenericObject) {
  loading.value = true;

  try {
    if ("contentable_type" in shareable.value) {
      await omniaSdk.v1.contents.share(shareable.value.id, {
        email_to: values["email_to"],
        message: values["message"],
        captcha: captchaToken.value,
      });
    }

    successToast("Contenuto condiviso con successo");
    opened.value = false;

    emits("success");
  } catch (error) {
    processError(error);
  }

  loading.value = false;
}
</script>

<template>
  <Dialog v-model:open="opened">
    <DialogTrigger as-child>
      <template v-if="$slots.trigger">
        <slot name="trigger" />
      </template>
      <template v-else>
        <Button>
          <ShareIcon class="size-4 me-2" />
          Condividi
        </Button>
      </template>
    </DialogTrigger>
    <DialogScrollContent class="max-w-screen-md" @pointer-down-outside.prevent>
      <DialogHeader>
        <DialogTitle>Condividi</DialogTitle>
        <DialogDescription
          class="text-base text-foreground mt-4 flex gap-2 items-start"
        >
          <InfoIcon class="size-5 text-primary-500 flex-shrink-0" />
          <p>
            Puoi condividere l'articolo con chiunque, anche con chi non è
            abbonato.<br />Compila i campi sottostanti: invieremo un'email ai
            destinatari con un link diretto per leggere il contenuto.
          </p>
        </DialogDescription>
      </DialogHeader>
      <div>
        <AutoForm
          :form="form"
          :schema="formSchema"
          :field-config="fieldConfig"
          class="content-card-grid grid gap-4"
          @submit="sendShare"
        >
          <div class="text-center">
            <NuxtTurnstile
              v-model="captchaToken"
              :options="{ theme: 'light' }"
            />
          </div>
          <DialogFooter>
            <DialogClose>
              <Button variant="ghost" type="reset">Annulla</Button>
            </DialogClose>
            <Button
              type="submit"
              :disabled="loading || !form.meta.value.valid"
              :class="{ 'animate-pulse': loading }"
            >
              Invia
            </Button>
          </DialogFooter>
        </AutoForm>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
